import { post } from "@src/appV2/api/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { ExtraWorkedTimeRequestStatus } from "@src/appV2/Shifts/MandatoryBreakPolicy/api/useExtraWorkedTimeRequests";
import { ExtraWorkedTimeType } from "@src/appV2/Shifts/Shift/types";
import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
  useQueryClient,
} from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { z } from "zod";

import { TIMEKEEPING_REASON_CONSTANTS } from "../Timekeeping/constants";
import { fullShiftExtraWorkedTimeRequestEntitySchema } from "./fullShiftExtraWorkedTimeRequestSchema";

const EXTRA_WORKED_TIME_REQUESTS_URL = `${environmentConfig.REACT_APP_BASE_API_URL}/extra-worked-time-requests`;

const createFullShiftExtraWorkedTimeRequestBodySchema = z.object({
  shiftId: z.string(),
  type: z.literal(ExtraWorkedTimeType.FULL_SHIFT),
  start: z.string().datetime(), // ISO 8601 — adjusted clock-in (earlier than original)
  reason: z
    .string()
    .min(TIMEKEEPING_REASON_CONSTANTS.MIN_CHARACTERS)
    .max(TIMEKEEPING_REASON_CONSTANTS.MAX_CHARACTERS),
});

const createFullShiftExtraWorkedTimeRequestResponseSchema = z.object({
  data: fullShiftExtraWorkedTimeRequestEntitySchema,
});

export type CreateFullShiftExtraWorkedTimeRequestBody = z.infer<
  typeof createFullShiftExtraWorkedTimeRequestBodySchema
>;

export type CreateFullShiftExtraWorkedTimeRequestResponse = z.infer<
  typeof createFullShiftExtraWorkedTimeRequestResponseSchema
>;

export function useCreateFullShiftExtraWorkedTimeRequest(
  options: UseMutationOptions<
    CreateFullShiftExtraWorkedTimeRequestResponse,
    AxiosError,
    CreateFullShiftExtraWorkedTimeRequestBody
  > = {}
): UseMutationResult<
  CreateFullShiftExtraWorkedTimeRequestResponse,
  AxiosError,
  CreateFullShiftExtraWorkedTimeRequestBody
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateFullShiftExtraWorkedTimeRequestBody) => {
      const response = await post({
        url: EXTRA_WORKED_TIME_REQUESTS_URL,
        data,
        requestSchema: createFullShiftExtraWorkedTimeRequestBodySchema,
        responseSchema: createFullShiftExtraWorkedTimeRequestResponseSchema,
      });
      return response.data;
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.CREATE_FULL_SHIFT_EXTRA_WORKED_TIME_REQUEST_FAILURE,
      userErrorMessage: "Failed to save your time adjustment. Please try again.",
    },
    ...options,
    onSuccess: (data, variables, context) => {
      // Seed the cache immediately so downstream pages (e.g. /unverified-timesheet) can read
      // the new PROVISIONAL extra worked time request without depending on a GET refetch that may fail in dev.
      queryClient.setQueryData(
        [
          EXTRA_WORKED_TIME_REQUESTS_URL,
          {
            "filter[shiftId]": variables.shiftId,
            "filter[status][]": ExtraWorkedTimeRequestStatus.PROVISIONAL,
          },
        ],
        {
          data: [
            {
              id: data.data.id,
              attributes: {
                start: data.data.attributes.start,
                reason: data.data.attributes.reason,
              },
            },
          ],
        }
      );
      // Still invalidate so production GET refetches replace the seeded cache with fresh data.
      void queryClient.invalidateQueries({ queryKey: [EXTRA_WORKED_TIME_REQUESTS_URL] });
      options.onSuccess?.(data, variables, context);
    },
  });
}
